import React, { useState, useRef } from "react";
import { useGame } from "@/contexts/GameContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ArrowLeft, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { motion, useAnimation } from "framer-motion";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Users, Settings } from "lucide-react";

import EnhancedPlayersList from "@/components/game/EnhancedPlayersList";

// Stałe
const SPIN_DURATION_S = 4; // Czas kręcenia w sekundach (dla framer-motion)
const MIN_ROTATIONS = 7;
const MAX_ROTATIONS = 18;
const SETTLE_DELAY_MS = 500;
const CIRCLE_RADIUS_PX = 140;
const JITTER_PERCENTAGE = 0.4; // 40% w lewo lub prawo od środka

export default function BottleSpinScreen({ onPlayerSelected }) {
  const { state, dispatch } = useGame();
  const [isSpinning, setIsSpinning] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const controls = useAnimation(); // Kontroler animacji framer-motion
  const rotationRef = useRef(0); // Przechowuje aktualny kąt obrotu
  const [showPlayers, setShowPlayers] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const currentPlayer = state.players[state.currentPlayerIndex];

  // Jeśli nie ma graczy (np. po resecie), wróć do startu
  if (!currentPlayer) {
    dispatch({ type: "SET_SCREEN", payload: "start" });
    return null; // Zwróć null, aby uniknąć błędów renderowania
  }

  const currentPlayerIndex = state.currentPlayerIndex;

  // Tworzymy listę graczy dostępnych do wylosowania (bez bieżącego gracza)
  const availablePlayers = state.players
    .map((player, index) => ({ player, originalIndex: index }))
    .filter((_, index) => index !== currentPlayerIndex);

  const numPlayers = availablePlayers.length;
  const degreesPerPlayer = numPlayers > 0 ? 360 / numPlayers : 0;

  // Funkcja rozpoczynająca kręcenie
  // Funkcja rozpoczynająca kręcenie
  const startSpin = async () => {
    // Sprawdzenie warunków brzegowych
    if (isSpinning || numPlayers === 0) {
      if (numPlayers === 0 && !isSpinning) {
        toast.info("Tylko Ty zostałeś - grasz sam ze sobą! 😉");
        onPlayerSelected(currentPlayerIndex);
      }
      return;
    }

    setIsSpinning(true);
    setSelectedIndex(null);

    // Losowanie zwycięzcy
    const winningIndex = Math.floor(Math.random() * numPlayers);
    const selectedPlayer = availablePlayers[winningIndex];

    const baseTargetAngle = winningIndex * degreesPerPlayer;

    // Obliczamy maksymalne odchylenie (np. 40% szerokości segmentu)
    const jitterMagnitude = degreesPerPlayer * JITTER_PERCENTAGE;
    // Losowe odchylenie od -jitterMagnitude/2 do +jitterMagnitude/2
    const jitter = (Math.random() - 0.5) * jitterMagnitude;
    // Kąt docelowy z uwzględnieniem "drżenia", z normalizacją (0-360)
    const targetAngle = (baseTargetAngle + jitter + 360) % 360;

    // Losowa liczba PEŁNYCH obrotów (używamy Math.floor)
    const randomRotations = Math.floor(
      MIN_ROTATIONS + Math.random() * (MAX_ROTATIONS - MIN_ROTATIONS)
    );
    const randomSpinDegrees = randomRotations * 360;

    const currentRotation = rotationRef.current;

    const baseAngle = (Math.floor(currentRotation / 360) + 1) * 360;

    // Końcowy kąt obrotu (Base + Spins + Target)
    const finalAngle = baseAngle + randomSpinDegrees + targetAngle;

    // Aktualizujemy ref, aby następne kręcenie zaczęło się od tej pozycji
    rotationRef.current = finalAngle;

    // Uruchomienie animacji za pomocą framer-motion
    await controls.start({
      rotate: finalAngle,
      transition: {
        duration: SPIN_DURATION_S,
        ease: "easeOut", // "easeOut" daje efekt spowalniania na końcu
      },
    });

    // Po zakończeniu animacji:
    setSelectedIndex(winningIndex);

    // Czekamy chwilę przed wybraniem gracza (efekt osadzenia)
    setTimeout(() => {
      onPlayerSelected(selectedPlayer.originalIndex);
      setIsSpinning(false);
    }, SETTLE_DELAY_MS);
  };

  // Funkcja obliczająca pozycję gracza na okręgu
  const getPlayerPosition = (index) => {
    // -90 stopni, aby pierwszy gracz był na górze (0°)
    const angleRad = (index * degreesPerPlayer - 90) * (Math.PI / 180);
    const x = CIRCLE_RADIUS_PX * Math.cos(angleRad);
    const y = CIRCLE_RADIUS_PX * Math.sin(angleRad);
    return {
      transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
    };
  };

  const endGame = () => {
    setShowMenu(false);
    dispatch({ type: "SET_SCREEN", payload: "end" });
  };

  const confirmEndGame = () => {
    setShowMenu(false);
    dispatch({ type: "SET_SCREEN", payload: "end" });
    dispatch({ type: "RESET_GAME" });
    toast.success("Gra została zresetowana!");
  };

  // Zmieniamy nazwę - RESET wraca do *początku*, nie do startu
  const goBackToMenu = () => {
    setShowMenu(false);
    dispatch({ type: "GO_BACK_TO_START" });
  };

  return (
    <>
      {/* Tytuł/Instrukcja */}

      <header className="w-full flex items-center justify-between mb-8">
        <Sheet open={showPlayers} onOpenChange={setShowPlayers}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon">
              <Users className="size-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="border-r border-white/10 p-0">
            <SheetHeader className="p-4 border-b border-white/10">
              <SheetTitle className="flex items-center gap-3 text-xl">
                <Users className="w-5 h-5 text-purple-400" /> Gracze w Grze
              </SheetTitle>
            </SheetHeader>
            <div className="p-4">
              <EnhancedPlayersList />
            </div>
          </SheetContent>
        </Sheet>
        {/* ... (Info o turze bez zmian) ... */}
        <div className="text-center">
          <div className="text-sm text-white/60">Tura gracza:</div>
          <div className="flex items-center justify-center gap-2">
            <span className="text-lg font-bold">{currentPlayer?.name}</span>
          </div>
        </div>
        {/* ... (Sheet Menu gry - zmiana 'resetGame' na 'goBackToMenu') ... */}
        <Sheet open={showMenu} onOpenChange={setShowMenu}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon">
              <Settings className="size-5" />{" "}
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="border-l border-white/10 p-0">
            <SheetHeader className="p-4 border-b border-white/10">
              <SheetTitle className="text-xl">Menu Gry</SheetTitle>
            </SheetHeader>
            <div className="flex-1 space-y-4 p-4">
              {/* Przycisk Powrót do Menu z AlertDialog */}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2"
                  >
                    <ArrowLeft className="size-5" /> Powrót do Menu
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Powrót do Menu Głównego?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      Czy na pewno chcesz wrócić do menu głównego? Twoja obecna
                      gra zostanie zapisana i będziesz mógł/mogła ją później
                      wznowić.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Anuluj</AlertDialogCancel>
                    <AlertDialogAction onClick={goBackToMenu}>
                      Tak, wróć
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              {/* Przycisk Zakończ Grę z AlertDialog */}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="destructive"
                    className="w-full justify-start gap-2"
                  >
                    <RotateCcw className="size-5" /> Zakończ Grę
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Zakończyć Grę?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Czy na pewno chcesz zakończyć obecną rozgrywkę? Jeśli
                      chcesz tylko wrócić do menu i móc wznowić grę, użyj opcji
                      "Powrót do Menu".
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Anuluj</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={confirmEndGame}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90" // Zachowanie stylu destructive
                    >
                      Tak, zakończ
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </SheetContent>
        </Sheet>
      </header>

      <main>
        <div className="text-center mb-6 relative z-10">
          <h1 className="text-3xl font-bold mb-2">Zakręć Butelką!</h1>
          <p className="text-base font-light max-w-md mx-auto">
            Kto tym razem odpowie na pytanie lub wykona wyzwanie?
          </p>
        </div>

        {/* Obszar Gry - Okrąg */}
        <div className="relative  h-full w-full bg-background/30 backdrop-blur-sm rounded-full flex items-center justify-center my-32">
          {/* Butelka na środku - używamy motion.div */}
          <motion.div
            className="relative w-40 h-40 rounded-full flex items-center justify-center border border-2 border-border transform-gpu z-10"
            animate={controls} // Podłączamy kontroler animacji
            initial={{ rotate: rotationRef.current }} // Ustawiamy początkowy obrót
          >
            {/* Emoji szampana wskazujące w górę */}
            <div className="text-5xl sm:text-5xl rotate-45">🍾</div>
            {/* Wskaźnik kierunku */}
            <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 w-1 h-1 border-l-[8px] border-r-[8px] border-b-[10px] border-transparent border-b-foreground drop-shadow-lg"></div>
          </motion.div>

          {/* Gracze wokół */}
          {availablePlayers.map((item, index) => (
            <div
              key={item.player.id}
              className={cn(
                "absolute z-10 top-1/2 left-1/2",
                "flex flex-col items-center p-2 rounded-lg",
                "transition-all duration-300 ease-in-out transform "
              )}
              style={getPlayerPosition(index)}
            >
              <Avatar
                className={cn(
                  "w-10 h-10 border border-2",
                  selectedIndex === index ? "border-green-500" : "border-none"
                )}
              >
                <AvatarFallback className="font-semibold">
                  {item.player.name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <p
                className={cn(
                  "font-medium text-xs mt-1 truncate max-w-[60px]",
                  selectedIndex === index ? "text-green-500" : "text-foreground"
                )}
              >
                {item.player.name}
              </p>
            </div>
          ))}

          {/* Komunikat, gdy nie ma graczy */}
          {availablePlayers.length === 0 && !isSpinning && (
            <p className="text-white/50 text-center">
              Dodaj więcej graczy, aby zakręcić!
            </p>
          )}
        </div>
      </main>

      {/* Przycisk Kręcenia */}
      <footer
        className={
          "w-full bg-background py-4 fixed bottom-0 left-0 right-0 z-20 " + // Dodano left-0 i z-20
          "border-t border-border " + // Subtelna linia oddzielająca na górze
          "shadow-[0_-6px_12px_-3px_rgba(0,0,0,0.2),_0_-4px_8px_-4px_rgba(0,0,0,0.05)] " + // Cień na górze
          "dark:shadow-[0_-6px_12px_-3px_rgba(0,0,0,0.8),_0_-4px_8px_-4px_rgba(0,0,0,0.15)]" // Cień dla trybu ciemnego
        }
      >
        <div className="max-w-xs mx-auto w-full flex justify-center items-center">
          <Button
            onClick={startSpin}
            disabled={isSpinning || availablePlayers.length === 0}
            size="lg"
            variant="success"
          >
            {isSpinning ? (
              <>
                <RotateCcw className="size-5 animate-spin" />
                Kręci się...
              </>
            ) : (
              <>🍾 Zakręć!</>
            )}
          </Button>
        </div>
      </footer>
    </>
  );
}
